import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'react-toastify'
import keycloak from '../../config/keycloak'

// Extend axios config to allow callers to suppress the global error toast
// and to pass a per-request org override without going through the headers merge.
export interface SuppressableRequestConfig extends AxiosRequestConfig {
  _suppressToast?: boolean
  /** Per-request org override — takes priority over the global crm_selected_org_id in localStorage. */
  _orgId?: string
}

const baseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api/v1'

if (!import.meta.env.VITE_API_BASE_URL && !import.meta.env.VITE_API_URL && import.meta.env.PROD) {
  console.warn('WARNING: VITE_API_BASE_URL is not defined in production build!')
}

// Main API instance
const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: { indexes: null },
})

// ── Request interceptor: attach Keycloak token & selected org header ──────────
// Keycloak is the single source of truth for the access token.
// updateToken(30) silently refreshes the token if it expires within 30 seconds.
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig & { _orgId?: string }) => {
    try {
      // Refresh if token will expire in <30s; no-op if still valid
      if (keycloak.authenticated) {
        await keycloak.updateToken(30)
      }
    } catch {
      // Refresh failed — force re-login
      keycloak.login()
      return Promise.reject(new Error('Keycloak token refresh failed; redirecting to login.'))
    }

    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`
    }

    try {
      const orgId = config._orgId ?? localStorage.getItem('crm_selected_org_id')
      if (orgId) {
        config.headers['X-Selected-Org-Id'] = orgId
      }
    } catch {
      // ignore localStorage errors
    }

    return config
  },
  (error) => Promise.reject(error),
)

// ── Response interceptor ──────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Token was rejected even after updateToken() — force re-login
      originalRequest._retry = true
      keycloak.login()
      return Promise.reject(error)
    }

    if (error.response?.status === 403) {
      toast.warn('Access denied. Syncing permissions...')
      import('../stores/authStore')
        .then(({ authStore }) => authStore.fetchRoleMenuPermissions())
        .catch(() => {})
    }

    // Generic error handling
    const suppress = (originalRequest as unknown as { _suppressToast?: boolean })?._suppressToast
    if (!suppress) {
      if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.')
      } else if (error.message === 'Network Error') {
        toast.error('Network error. Please check your connection.')
      }
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
